import type { QueryKey, QueryMeta } from '@tanstack/react-query';
import { queryClient } from './constants.js';
import { queryPolicies } from './queryPolicies.js';

interface StructuralPageQueryOptions<T> {
  queryKey: QueryKey;
  queryFn: () => Promise<T>;
  staleTime?: number;
  gcTime?: number;
  meta?: QueryMeta;
}

/** A statement timeout is deterministic: the same read times out again. */
export function isStatementTimeout(error: unknown): boolean {
  return (error as { code?: string } | null)?.code === '57014'
    || /statement timeout/i.test(String((error as Error | null)?.message ?? ''));
}

// One retry for transient failures (the client default), none for a timeout
// (docs/database-query-performance.md, request amplification).
const retryUnlessTimeout = (failures: number, error: unknown) => failures < 1 && !isStatementTimeout(error);

/** Shared imperative React Query path for ordinary-table and report-grid blocks. */
export function fetchStructuralPage<T>({
  queryKey,
  queryFn,
  staleTime = queryPolicies.pagedData.staleTime,
  gcTime = queryPolicies.pagedData.gcTime,
  meta,
}: StructuralPageQueryOptions<T>): Promise<T> {
  return queryClient.fetchQuery({ queryKey, queryFn, staleTime, gcTime, meta, retry: retryUnlessTimeout });
}

